import React, { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { LegendDot, LegendItem, LegendRow } from './styles';
import { NATIVE_FLOORPLAN_PLACEMENT_COLORS } from '../const';
import { useCoworkerGroups } from '../hooks/use-coworker-groups.hook';
import { useCoworkerVisibility } from 'floorplan/hooks/use-coworker-visibility.hook';

/**
 * Read by `PlacementDetailsModal` (styles.ts) to sit its sheet just clear of this row's real
 * rendered height — whatever that currently is, one line or two (`LegendRow` wraps). Set on `<html>`
 * rather than passed as a prop because the sheet is portaled straight to `document.body` and so
 * shares no DOM ancestor with this row to measure against directly.
 */
const LEGEND_HEIGHT_CSS_VAR = '--rb-floorplan-legend-height';

type LegendKey = keyof typeof NATIVE_FLOORPLAN_PLACEMENT_COLORS;

interface LegendEntry {
  key: LegendKey | 'group';
  /** Either a message id… */
  labelId?: string;
  /** …or a literal label (the active group's own name). */
  label?: string;
  colors: { fill: string; stroke: string };
}

const BASE_ITEMS: LegendEntry[] = [
  { key: 'available', labelId: 'available', colors: NATIVE_FLOORPLAN_PLACEMENT_COLORS.available },
  { key: 'yourBooking', labelId: 'yourBooking', colors: NATIVE_FLOORPLAN_PLACEMENT_COLORS.yourBooking },
  { key: 'unavailable', labelId: 'unavailable', colors: NATIVE_FLOORPLAN_PLACEMENT_COLORS.unavailable },
];

/**
 * Focus mode (§C3): purple means "one of <group>" — the only place a coworker is drawn — so the
 * group's own name is listed, after "Your booking"; every other taken desk is plain Unavailable.
 * Unfiltered the map draws no coworkers and the legend has no coworker entry.
 */
const focusModeItems = (groupName: string): LegendEntry[] => [
  ...BASE_ITEMS.slice(0, 2),
  { key: 'group', label: groupName, colors: NATIVE_FLOORPLAN_PLACEMENT_COLORS.coworker },
  ...BASE_ITEMS.slice(2),
];

export const FloorplanLegend = (): JSX.Element => {
  const intl = useIntl();
  const { effectiveName } = useCoworkerVisibility();
  const { activeGroup } = useCoworkerGroups();
  const rowRef = useRef<HTMLDivElement>(null);

  // Keeps the published height current across a resize, an orientation change, or the legend's own
  // content changing (a group name arriving, the coworker item toggling on) — anything that could
  // move it between one line and two. `ResizeObserver` isn't in jsdom, so this degrades to a single
  // measurement on mount there, which is all the tests need.
  useEffect(() => {
    const node = rowRef.current;

    if (!node) {
      return undefined;
    }

    const publishHeight = (): void => {
      document.documentElement.style.setProperty(LEGEND_HEIGHT_CSS_VAR, `${node.offsetHeight}px`);
    };

    publishHeight();

    if (typeof ResizeObserver === 'undefined') {
      return () => document.documentElement.style.removeProperty(LEGEND_HEIGHT_CSS_VAR);
    }

    const observer = new ResizeObserver(publishHeight);
    observer.observe(node);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(LEGEND_HEIGHT_CSS_VAR);
    };
  }, []);

  const items = useMemo(() => {
    let entries = BASE_ITEMS;

    // Focus mode is a by-name feature; with names hidden the legend must not name a group either.
    if (activeGroup && effectiveName) {
      entries = focusModeItems(activeGroup.name);
    }

    return entries.map(entry => ({
      ...entry,
      label: entry.label ?? intl.formatMessage({ id: entry.labelId as string }),
    }));
  }, [intl, effectiveName, activeGroup]);

  return (
    <LegendRow ref={rowRef} data-testid="floorplan-legend">
      {items.map(({ key, label, colors }) => (
        <LegendItem key={key} data-testid={`floorplan-legend-${key}`}>
          <LegendDot fill={colors.fill} stroke={colors.stroke} />
          {label}
        </LegendItem>
      ))}
    </LegendRow>
  );
};
